import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

export default function RulesEngineFormSkeleton() {
  const theme = useTheme();

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* Main Content Area */}
      <Stack
        direction="row"
        sx={{ height: '100%', width: '100%', pt: '24px', gap: 3 }}
      >
        {/* Left Side (Steps) */}
        <Box sx={{ flex: 79, minWidth: 0, width: '100%' }}>
          <Stack spacing="24px" sx={{ width: '100%' }}>
            {[...Array(4)].map((_, index) => (
              <Card
                key={index}
                sx={{
                  width: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 1px 6px 0 rgba(0, 0, 0, 0.12)',
                }}
              >
                <CardHeader
                  avatar={
                    <Box
                      sx={{
                        backgroundColor: theme.palette.semantic.surface.violet,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                      }}
                    >
                      <Skeleton
                        variant="rounded"
                        width={24}
                        height={24}
                        sx={{
                          backgroundColor: theme.palette.semantic.icon.violet,
                        }}
                      />
                    </Box>
                  }
                  title={<Skeleton variant="text" width={200} height={32} />}
                  subheader={
                    <Skeleton variant="text" width={300} height={20} />
                  }
                  sx={{
                    borderBottom: `1px solid ${theme.palette.semantic.outline.light}`,
                  }}
                />
                <CardContent sx={{ padding: '24px 36px' }}>
                  <Stack spacing={2}>
                    <Skeleton variant="rounded" width="100%" height={56} />
                    {index > 0 && (
                      <Skeleton variant="rounded" width="100%" height={100} />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>

        {/* Right Side (Rule Summary Panel) */}
        <Box
          sx={{
            flex: 21,
            display: { xs: 'none', lg: 'block' },
            position: 'sticky',
            top: 110,
            alignSelf: 'flex-start',
            height: '100%',
          }}
        >
          <Card
            elevation={1}
            sx={{
              width: '100%',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              p: 1,
              px: 2,
              height: 'fit-content',
            }}
          >
            {/* Scrollable top: Rule Summary */}
            <Box
              sx={{
                flex: '0 1 auto',
                minHeight: 0,
                pt: 1,
                px: 0.5,
                pb: 2,
              }}
            >
              <Skeleton
                variant="text"
                width={150}
                height={28}
                sx={{ mb: 0.5 }}
              />

              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="80%" height={20} />

              <Box
                sx={{
                  mt: 1.5,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  borderRadius: 1,
                  bgcolor: theme.palette.semantic.surface.info,
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                }}
              >
                <Skeleton variant="text" width="100%" height={16} />
                <Skeleton variant="text" width="100%" height={16} />
                <Skeleton variant="text" width="80%" height={16} />
              </Box>

              {/* WHEN */}
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton variant="text" width={60} height={20} />
                </Stack>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    px: 1,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between">
                    <Skeleton variant="text" width={60} height={16} />
                    <Skeleton variant="text" width={120} height={16} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Skeleton variant="text" width={80} height={16} />
                    <Skeleton variant="text" width={60} height={16} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Skeleton variant="text" width={60} height={16} />
                    <Skeleton variant="text" width={20} height={16} />
                  </Stack>
                </Box>
              </Box>

              {/* IF */}
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton variant="text" width={40} height={20} />
                </Stack>
                <Box sx={{ px: 1 }}>
                  <Skeleton variant="text" width={20} height={16} />
                </Box>
              </Box>

              {/* THEN */}
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <Skeleton variant="circular" width={16} height={16} />
                  <Skeleton variant="text" width={60} height={20} />
                </Stack>
                <Box sx={{ px: 1 }}>
                  <Skeleton variant="text" width={20} height={16} />
                </Box>
              </Box>
            </Box>

            {/* Fixed bottom: Estimated Impact */}
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                pt: 2,
                pr: 1,
                pb: 1.5,
                pl: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack direction="column" spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Skeleton variant="text" width={120} height={20} />
                  <Skeleton variant="circular" width={18} height={18} />
                </Stack>

                <Skeleton variant="rounded" width="100%" height={40} />
                <Skeleton variant="rounded" width="100%" height={36} />

                <Stack alignItems="center" spacing={1} mt={1}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={100} height={24} />
                  <Skeleton variant="text" width="100%" height={16} />
                  <Skeleton variant="text" width="80%" height={16} />
                </Stack>
              </Stack>
            </Box>
          </Card>
        </Box>
      </Stack>
    </Box>
  );
}
